import { useState } from 'react'
import { supabase } from '../../../../lib/supabase'
import { notifyAllSupporters } from '../../../../lib/notificationHelpers'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function FixtureForm({ fixture, onClose, onRefresh }) {
  const [formData, setFormData] = useState({
    home_team: fixture?.home_team || '',
    away_team: fixture?.away_team || '',
    match_date: fixture?.match_date ? new Date(fixture.match_date).toISOString().slice(0, 16) : '',
    venue: fixture?.venue || '',
    competition: fixture?.competition || '',
    status: fixture?.status || 'scheduled',
    home_score: fixture?.home_score ?? '',
    away_score: fixture?.away_score ?? '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const payload = {
      home_team: formData.home_team,
      away_team: formData.away_team,
      match_date: formData.match_date,
      venue: formData.venue,
      competition: formData.competition,
      status: formData.status,
      home_score: formData.status === 'completed' ? Number(formData.home_score) : null,
      away_score: formData.status === 'completed' ? Number(formData.away_score) : null,
      club_id: CLUB_ID,
    }

    const { error } = fixture
      ? await supabase.from('fixtures').update(payload).eq('id', fixture.id)
      : await supabase.from('fixtures').insert(payload)

    if (error) { setError(error.message); setLoading(false); return }

    if (!fixture) {
      try {
        await notifyAllSupporters({
          clubId: CLUB_ID,
          title: 'New Fixture Added',
          message: `${payload.home_team} vs ${payload.away_team} on ${new Date(payload.match_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
          type: 'fixture',
          link: '/fixtures',
        })
      } catch (err) {
        console.error('Notification failed:', err)
      }
    }

    onRefresh()
    onClose()
  }

  const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 p-8 w-full max-w-lg overflow-y-auto max-h-screen">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-black text-xl uppercase tracking-tight text-gray-900 dark:text-white">
            {fixture ? 'Edit Fixture' : 'Add Fixture'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">✕</button>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Home Team</label>
              <input type="text" name="home_team" value={formData.home_team} onChange={handleChange} placeholder="Mighty Wanderers" required className={inputClass} />
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Away Team</label>
              <input type="text" name="away_team" value={formData.away_team} onChange={handleChange} placeholder="Away Team" required className={inputClass} />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className={labelClass}>Match Date & Time</label>
            <input type="datetime-local" name="match_date" value={formData.match_date} onChange={handleChange} required className={inputClass} />
          </div>

          <div className="flex flex-col gap-2">
            <label className={labelClass}>Venue</label>
            <input type="text" name="venue" value={formData.venue} onChange={handleChange} placeholder="Shinde Stadium" className={inputClass} />
          </div>

          <div className="flex flex-col gap-2">
            <label className={labelClass}>Competition</label>
            <input type="text" name="competition" value={formData.competition} onChange={handleChange} placeholder="FAZ Super League" className={inputClass} />
          </div>

          <div className="flex flex-col gap-2">
            <label className={labelClass}>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {formData.status === 'completed' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Home Score</label>
                <input type="number" name="home_score" value={formData.home_score} onChange={handleChange} placeholder="0" min="0" className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Away Score</label>
                <input type="number" name="away_score" value={formData.away_score} onChange={handleChange} placeholder="0" min="0" className={inputClass} />
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">
              {loading ? 'Saving...' : fixture ? 'Update Fixture' : 'Add Fixture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}